// src/auth/AuthContext.tsx
import {useEffect, useRef} from 'react';
import {useApiStore, useAuthApi} from "./ApiContext.tsx";
import {Me} from "../api/ApiTypes.ts";
import {create} from "zustand";


type AuthState = {
    me: Me | null
    setMe: (me: Me | null) => void
    isInitializing: boolean
    setInitializing: (v: boolean) => void
}

export const useAuthStore = create<AuthState>((set) => ({
    me: null,
    setMe: (me) => set({ me }),
    isInitializing: true,
    setInitializing: (v) => set({ isInitializing: v }),
}))


/** Bootstraps auth once at app start and re-checks on 401 flag */
export function AuthBootstrap() {
    const { checkAuth } = useAuthApi()
    const setMe = useAuthStore((s) => s.setMe)
    const setInitializing = useAuthStore((s) => s.setInitializing)
    const isUnauthorized = useApiStore((s) => s.isUnauthorized)

    // Guard against React.StrictMode double-mount
    const didRun = useRef(false)

    useEffect(() => {
        console.debug("did run", didRun.current)
        if (didRun.current) return
        didRun.current = true;

        (async () => {
            console.debug("checking auth, setting initilizing to true")
            setInitializing(true)
            try {
                const me = await checkAuth() // your useAuthApi.checkAuth sets isUnauthorized=false on success
                setMe(me)
            } catch {
                setMe(null)
            } finally {
                setInitializing(false)
            }
        })()
    }, [checkAuth, setMe, setInitializing])

    // Re-check when API store flips to unauthorized (401)
    useEffect(() => {
        if (!isUnauthorized) return;
        console.log("isUnauthorized changed, going to check auth again", isUnauthorized);

        (async () => {
            try {
                const me = await checkAuth()
                setMe(me) // checkAuth will clear isUnauthorized on success
            } catch {
                setMe(null)
            }
        })()
    }, [isUnauthorized, checkAuth, setMe])

    return null
}

export function useAuth() {
    const me = useAuthStore((s) => s.me)
    const setMe = useAuthStore((s) => s.setMe)
    const isInitializing = useAuthStore((s) => s.isInitializing)
    return { me, setMe, isInitializing }
}